import { useRef, useState, type FormEvent } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  KeyRound,
  LoaderCircle,
  MailCheck,
} from "lucide-react";
import { errorMessage, post, RequestError } from "../api";
import { ErrorNotice, Field, SolMark } from "../ui";
import "./identity.css";

export type IdentityLink = {
  purpose: "verification" | "recovery";
  token: string | null;
};

export function readIdentityLink(): IdentityLink | null {
  const purpose =
    window.location.pathname === "/verify-email"
      ? "verification"
      : window.location.pathname === "/reset-password"
        ? "recovery"
        : null;
  if (!purpose) return null;
  const candidate = window.location.hash.slice(1);
  return {
    purpose,
    token: /^[A-Za-z0-9_-]{43}$/.test(candidate) ? candidate : null,
  };
}

export function IdentityLinkScreen({
  link,
  onReturn,
  onReset,
  onConsumed,
}: {
  link: IdentityLink;
  onReturn: (request?: "verification" | "recovery") => void;
  onReset: () => void;
  onConsumed: () => void;
}) {
  // Keep link secrets in memory, remove them after successful use, and never
  // consume a link automatically: mail scanners and React effects can revisit it.
  const token = useRef(link.token);
  const submitting = useRef(false);
  const [pending, setPending] = useState(false);
  const [complete, setComplete] = useState(false);
  const [invalid, setInvalid] = useState(!link.token);
  const [error, setError] = useState("");
  const recovery = link.purpose === "recovery";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current || !token.current) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const password = String(form.get("password") ?? "");
    if (recovery && password !== form.get("confirmPassword")) {
      setError("Your passwords don’t match. Please enter them again.");
      return;
    }
    submitting.current = true;
    setPending(true);
    setError("");
    try {
      await post<void>(`/auth/${link.purpose}/confirm`, {
        token: token.current,
        ...(recovery ? { password } : {}),
      });
      token.current = null;
      onConsumed();
      formElement.reset();
      setComplete(true);
      if (recovery) onReset();
    } catch (failure) {
      if (failure instanceof RequestError && failure.code === "INVALID_TOKEN") {
        token.current = null;
        onConsumed();
        setInvalid(true);
      } else setError(errorMessage(failure));
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };
  return (
    <main className="identity-page">
      <div className="brand">
        <SolMark />
        <span>
          sol<span className="brand-office">office</span>
        </span>
      </div>
      <section className="identity-card" aria-labelledby="identity-heading">
        <span className="identity-symbol" aria-hidden="true">
          {complete ? (
            <Check size={30} />
          ) : recovery ? (
            <KeyRound size={30} />
          ) : (
            <MailCheck size={30} />
          )}
        </span>
        <span className="eyebrow">YOUR SOL OFFICE ACCOUNT</span>
        <h1 id="identity-heading">
          {complete
            ? recovery
              ? "A fresh start."
              : "You’re verified."
            : invalid
              ? "Let’s get a new link."
              : recovery
                ? "Choose a new password."
                : "Confirm your email."}
        </h1>
        <p>
          {complete
            ? recovery
              ? "Your password has been updated and all previous sessions have been signed out. Sign in with your new password."
              : "Your email address is confirmed. You can now sign in to your workspace."
            : invalid
              ? "This link is missing, expired, or already used. Request another link to continue."
              : recovery
                ? "Make it memorable for you and difficult for someone else to guess."
                : "Finish setting up your account by confirming that this email address belongs to you."}
        </p>
        {complete ? (
          <div role="status">
            <button
              className="button primary auth-submit"
              onClick={() => onReturn()}
            >
              Continue to sign in <ArrowRight size={18} />
            </button>
          </div>
        ) : invalid ? (
          <button
            className="button primary auth-submit"
            onClick={() => onReturn(link.purpose)}
          >
            Request a new link <ArrowRight size={18} />
          </button>
        ) : (
          <form className="auth-form" onSubmit={submit}>
            {recovery && (
              <>
                <Field
                  label="New password"
                  hint="At least 12 characters. A memorable phrase works well."
                >
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                    autoFocus
                  />
                </Field>
                <Field label="Confirm new password">
                  <input
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                  />
                </Field>
              </>
            )}
            {error && <ErrorNotice message={error} />}
            <button
              className="button primary auth-submit"
              disabled={pending}
              type="submit"
            >
              {pending && <LoaderCircle size={18} className="spin" />}
              {recovery ? "Update password" : "Verify email address"}
              {!pending && <ArrowRight size={18} />}
            </button>
          </form>
        )}
        {!complete && (
          <button
            className="identity-back"
            onClick={() => onReturn()}
            disabled={pending}
          >
            <ArrowLeft size={16} /> Back to sign in
          </button>
        )}
      </section>
      <p className="identity-footer">A thoughtful place to work.</p>
    </main>
  );
}
